import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRightLeft, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { LaunchCard, StatusBadge } from "./ui";

const renderValue = (value) => JSON.stringify(value, null, 2);

export function SettingsPromotionCard() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const hasLoaded = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/admin/settings-promotion/preview");
      setData(payload);
      setSelected(Object.fromEntries(
        (payload.categories || [])
          .filter((category) => category.changed)
          .map((category) => [category.key, false]),
      ));
    } catch (error) {
      if (error.response?.status === 403) {
        setData({
          available: false,
          message: "Settings promotion is available to system administrators.",
        });
      } else {
        toast.error(formatApiError(error.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;
    load();
  }, [load]);

  const selectedKeys = useMemo(
    () => Object.entries(selected).filter(([, checked]) => checked).map(([key]) => key),
    [selected],
  );

  const promote = async () => {
    setConfirming(false);
    setSending(true);
    try {
      await api.post("/admin/settings-promotion/promote", {
        categories: selectedKeys,
        note: note.trim(),
      });
      toast.success("Selected settings promoted to the paired production portal");
      setNote("");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <LaunchCard
      eyebrow="Settings promotion"
      title="Review settings for production"
      testId="settings-promotion-card"
      aside={
        <StatusBadge
          status={data?.available ? "pass" : "warn"}
          label={data?.available ? "Ready to review" : "Waiting"}
          testId="settings-promotion-status"
        />
      }
    >
      <p className="text-sm mb-4 max-w-3xl" style={{ color: "var(--muted)" }}>
        Review selected portal settings before they move from development to the paired production
        site. Code publishing remains a separate hosting action. Promotions add or update matching
        view and template names; production-only entries remain untouched.
      </p>
      {!data || loading ? (
        <div
          className="text-sm"
          style={{ color: "var(--muted)" }}
          data-testid="settings-promotion-loading"
        >
          Checking the paired site…
        </div>
      ) : !data.available ? (
        <div
          className="border px-3 py-3 text-sm"
          style={{ borderColor: "var(--border)", color: "var(--muted)" }}
          data-testid="settings-promotion-unavailable"
        >
          {data.message}
        </div>
      ) : (
        <>
          <div className="mb-4 text-xs" style={{ color: "var(--muted)" }}>
            Destination: <span
              className="font-mono"
              data-testid="settings-promotion-destination"
            >
              {data.destination}
            </span>
          </div>
          <div
            className="border"
            style={{ borderColor: "var(--border)" }}
            data-testid="settings-promotion-list"
          >
            {data.categories.map((category) => (
              <div
                key={category.key}
                className="border-b p-4 last:border-b-0"
                style={{ borderColor: "var(--border)" }}
                data-testid={`settings-promotion-row-${category.key}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(selected[category.key])}
                      disabled={!category.changed}
                      onChange={(event) => setSelected((current) => ({
                        ...current,
                        [category.key]: event.target.checked,
                      }))}
                      data-testid={`settings-promotion-select-${category.key}`}
                    />
                    <span>
                      <span
                        className="block font-semibold text-sm"
                        style={{ color: "var(--ink)" }}
                      >
                        {category.label}
                      </span>
                      <span
                        className="block text-xs mt-1"
                        style={{ color: "var(--muted)" }}
                      >
                        Development: {category.source_summary}
                      </span>
                      <span className="block text-xs" style={{ color: "var(--muted)" }}>
                        Production: {category.destination_summary}
                      </span>
                    </span>
                  </label>
                  <StatusBadge
                    status={category.changed ? "warn" : "pass"}
                    label={category.changed ? "Changed" : "Matches"}
                    testId={`settings-promotion-state-${category.key}`}
                  />
                </div>
                {category.warning && (
                  <p className="mt-3 text-xs" style={{ color: "var(--muted)" }}>
                    {category.warning}
                  </p>
                )}
                <details
                  className="mt-3 text-xs"
                  data-testid={`settings-promotion-values-${category.key}`}
                >
                  <summary
                    className="cursor-pointer font-semibold"
                    style={{ color: "var(--ink)" }}
                  >
                    View before and after
                  </summary>
                  <div className="grid gap-3 mt-3 md:grid-cols-2">
                    <div>
                      <div className="eyebrow text-[9px] mb-1">Production before</div>
                      <pre
                        className="overflow-auto border p-3 whitespace-pre-wrap"
                        style={{ borderColor: "var(--border)", color: "var(--muted)" }}
                      >
                        {renderValue(category.destination)}
                      </pre>
                    </div>
                    <div>
                      <div className="eyebrow text-[9px] mb-1">Development selected</div>
                      <pre
                        className="overflow-auto border p-3 whitespace-pre-wrap"
                        style={{ borderColor: "var(--border)", color: "var(--ink)" }}
                      >
                        {renderValue(category.source)}
                      </pre>
                    </div>
                  </div>
                </details>
              </div>
            ))}
          </div>
          <label className="field-label mt-5" htmlFor="settings-promotion-note">
            Audit note
          </label>
          <textarea
            id="settings-promotion-note"
            rows={3}
            className="input-flat mt-2"
            value={note}
            maxLength={280}
            onChange={(event) => setNote(event.target.value)}
            placeholder="e.g. Reviewed wording and release switches for the November rehearsal"
            data-testid="settings-promotion-note"
          />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn-primary flex items-center gap-2"
              disabled={sending || selectedKeys.length === 0 || note.trim().length < 8}
              onClick={() => setConfirming(true)}
              data-testid="settings-promotion-promote-btn"
            >
              <Send size={14} /> {sending ? "Promoting…" : "Promote selected settings"}
            </button>
            <button
              type="button"
              className="btn-ghost flex items-center gap-2"
              disabled={loading || sending}
              onClick={load}
              data-testid="settings-promotion-refresh-btn"
            >
              <RefreshCw size={14} /> Refresh comparison
            </button>
            <span className="text-xs flex items-center gap-1" style={{ color: "var(--muted)" }}>
              <ArrowRightLeft size={12} /> {selectedKeys.length} selected
            </span>
          </div>
        </>
      )}
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="sm:max-w-lg" data-testid="settings-promotion-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Promote reviewed settings?</DialogTitle>
            <DialogDescription className="text-sm">
              {selectedKeys.length} selected setting group(s) will move to the paired production site.
              Application code, client data, files, accounts, sessions, and secrets are excluded.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setConfirming(false)}
              data-testid="settings-promotion-confirm-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={sending}
              onClick={promote}
              data-testid="settings-promotion-confirm-submit"
            >
              {sending ? "Promoting…" : "Confirm promotion"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </LaunchCard>
  );
}